import { useState } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ImportConflict, importMonthlyData } from '../../services/monthlyDataImport';
import toast from 'react-hot-toast';

interface ExcelImportPreviewProps {
  data: any[];
  conflicts: ImportConflict[];
  onImportComplete: () => void;
  onCancel: () => void;
}

export const ExcelImportPreview = ({ 
  data, 
  conflicts, 
  onImportComplete, 
  onCancel 
}: ExcelImportPreviewProps) => {
  const [loading, setLoading] = useState(false);
  const [conflictResolution, setConflictResolution] = useState<'update' | 'skip' | 'create_new'>('skip');

  // Nombres de los meses para mostrar en la interfaz
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const handleImport = async () => {
    setLoading(true);
    try {
      const result = await importMonthlyData(data, conflictResolution);
      
      if (result.errors.length > 0) {
        toast.error(`Se encontraron ${result.errors.length} errores durante la importación`);
        console.error('Errores de importación:', result.errors);
      } else {
        toast.success(
          `Importación completada: ${result.created} registros creados, ${result.updated} actualizados, ${result.skipped} omitidos`
        );
        onImportComplete();
      }
    } catch (error: any) {
      console.error('Error al importar datos:', error);
      toast.error(error.message || 'Error al importar datos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">Vista Previa de Importación</h2>
        
        <div className="space-y-6">
          {/* Resumen de la importación */}
          <div>
            <h3 className="text-lg font-medium mb-2">Resumen</h3>
            <div className="bg-gray-50 p-3 rounded-md">
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li>Total de registros a importar: <span className="font-medium">{data.length}</span></li>
                <li>Registros sin conflictos: <span className="font-medium">{data.length - conflicts.length}</span></li>
                <li>Registros con conflictos: <span className="font-medium text-amber-600">{conflicts.length}</span></li>
              </ul>
            </div>
          </div>
          
          {/* Opciones de resolución de conflictos */}
          {conflicts.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-2">Resolución de Conflictos</h3>
              <p className="text-sm text-gray-500 mb-3">
                Se encontraron registros existentes para algunos de los datos que intentas importar.
                Selecciona cómo quieres manejar estos conflictos:
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="skip"
                    name="conflict-resolution"
                    value="skip"
                    checked={conflictResolution === 'skip'}
                    onChange={() => setConflictResolution('skip')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="skip" className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Omitir</span> - No importar registros que ya existen
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="update"
                    name="conflict-resolution"
                    value="update"
                    checked={conflictResolution === 'update'}
                    onChange={() => setConflictResolution('update')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="update" className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Actualizar</span> - Sobrescribir registros existentes con los nuevos datos
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="create_new"
                    name="conflict-resolution"
                    value="create_new"
                    checked={conflictResolution === 'create_new'}
                    onChange={() => setConflictResolution('create_new')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="create_new" className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Crear duplicados</span> - Importar todos los registros (puede causar duplicados)
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Lista de conflictos */}
          {conflicts.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-2">Detalles de Conflictos</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fila
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Propiedad
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Periodo
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Datos Existentes
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Datos Nuevos
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {conflicts.map((conflict, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {conflict.row}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {conflict.propertyName}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {monthNames[conflict.month - 1]} {conflict.year}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500">
                          <div>Facturación: <span className="font-medium">{conflict.existing.revenue}€</span></div>
                          <div>Ocupación: <span className="font-medium">{conflict.existing.occupancyRate}%</span></div>
                          <div>Beneficio: <span className="font-medium">{conflict.existing.profit}€</span></div>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500">
                          <div>Facturación: <span className="font-medium">{conflict.new.revenue}€</span></div>
                          <div>Ocupación: <span className="font-medium">{conflict.new.occupancyRate}%</span></div>
                          <div>Beneficio: <span className="font-medium">{conflict.new.profit}€</span></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Botones de acción */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              onClick={onCancel}
              variant="outline"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleImport}
              loading={loading}
              variant="primary"
            >
              Confirmar Importación
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
